import {
  create,
  get,
  parseCreationOptionsFromJSON,
  parseRequestOptionsFromJSON,
  RegistrationPublicKeyCredential,
} from '@github/webauthn-json/browser-ponyfill';
import {
  AuthService,
  BeginWebAuthnLoginResponse,
  BeginWebAuthnRegistrationResponse,
  CompleteWebAuthnLoginRequest,
  CompleteWebAuthnLoginResponse,
  CompleteWebAuthnRegistrationRequest,
  CompleteWebAuthnRegistrationResponse,
  VerifyAuthResponse,
} from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import {
  AuthStorage,
  localStorageHelper,
  LOGIN_ERROR_CAUSE,
  SignInMethod,
  isWeaveTokenActive,
  setWeaveToken,
  getExpireLength,
} from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { sentry } from '@frontend/tracking';
import { getLoggedOutUserStore } from '../utils/loggedOutUserStore';
import { navigateToUrl } from '../utils/navigateToUrl';
import { AuthFlow, SignInProps, SignOutProps, ClientTokens, HandleCallbackProps } from './AuthFlow';
import { APIFetchNoAuth, AuthFetchHTTPError } from './fetch';

export class LegacyClient implements AuthFlow {
  public getSignInMethod(): Promise<SignInMethod> {
    return Promise.resolve('legacy');
  }

  public getTokens(): Promise<ClientTokens> {
    const weaveJWT = localStorageHelper.get<string>(AuthStorage.weave_token);
    const tokens = {
      weaveToken: weaveJWT,
    };
    return Promise.resolve(tokens);
  }

  public handleCallback(_?: HandleCallbackProps): Promise<ClientTokens> {
    return Promise.resolve(this.getTokens());
  }

  public isUserAuthenticated(): boolean {
    return isWeaveTokenActive();
  }

  public async signIn(signInInfo?: SignInProps): Promise<VerifyAuthResponse> {
    if (!!signInInfo?.useWebauthn) {
      return this.beginSignInWithWebauthn();
    }

    const store = getLoggedOutUserStore(signInInfo?.username || '');
    const fallback = localStorageHelper.get(AuthStorage.cached_challenge) ?? '';
    const previousChallenge = store.getItem<string>(
      AuthStorage.cached_challenge,
      fallback // Fallback to the cached challenge if it exists. If it's for a different user, the backend will ignore it.
    );

    return AuthService.VerifyAuth(
      APIFetchNoAuth(appConfig.BACKEND_API) as (url: string, reqInit: RequestInit) => Promise<VerifyAuthResponse>,
      {
        data: {
          credentials: {
            username: signInInfo?.username,
            password: signInInfo?.password,
          },
          code: signInInfo?.code,
        },
        challenge: signInInfo?.challenge,
        previousChallenge, // sends the cached challenge if it exists
      }
    )
      .then((res) => {
        if (res.token) {
          // successful login
          setWeaveToken(res.token, res.jtiToken || '');
          if (!!signInInfo?.code && signInInfo?.challenge) {
            // if we have a code and challenge, we are doing MFA
            // store the challenge for cached MFA logins
            store.setItem(AuthStorage.cached_challenge, signInInfo.challenge);
          }
          if (signInInfo?.registerWebauthn) {
            return this.handleWebauthnRegistration(res.token).then(() => res);
          }
          return res;
        } else if (res.mfa) {
          return res;
        } else {
          throw new Error('No token returned');
        }
      })
      .catch((err: AuthFetchHTTPError) => {
        if (err?.status === 403 && err?.message.includes('IP address is not authorized')) {
          err.cause = LOGIN_ERROR_CAUSE.ipRestricted;
          throw err;
        }
        const isExpectedWebauthnCancellation =
          !!signInInfo?.registerWebauthn && this.isExpectedWebauthnCancellation(err);
        const isExpiredLoginChallenge = err?.status === 400 && err?.message?.includes('Invalid challenge id');
        if (!isExpectedWebauthnCancellation) {
          const logToSentry = isExpiredLoginChallenge ? sentry.warn : sentry.error;
          logToSentry({
            topic: 'auth',
            team: 'users-infra',
            error: err,
            addContext: {
              name: 'auth',
              context: {
                errMessage: 'Fetching verify token',
              },
            },
          });
        }
        err.cause = LOGIN_ERROR_CAUSE.unknown;
        throw err;
      });
  }

  public async signOut(props?: SignOutProps): Promise<void> {
    localStorageHelper.delete(AuthStorage.weave_token);
    localStorageHelper.delete(AuthStorage.token_received_at);
    if (props?.logoutRedirect) {
      navigateToUrl(props?.logoutRedirect, { reason: 'legacy signOut redirect' });
    } else {
      navigateToUrl('/', { reason: 'legacy signOut redirect, default to /' }); // we default to the home page
    }
  }

  private async beginSignInWithWebauthn(): Promise<VerifyAuthResponse> {
    return AuthService.BeginWebAuthnLogin(
      this.wrappedFetch() as (url: string, reqInit: RequestInit) => Promise<BeginWebAuthnLoginResponse>,
      {}
    ).then((data) => {
      let jsonAssertions = '';

      if (data.assertionData) {
        jsonAssertions = atob(data.assertionData);
      }

      const parsedAssertions = JSON.parse(jsonAssertions);
      const assertions = parseRequestOptionsFromJSON(parsedAssertions);

      // webauthn login challenge
      return get(assertions)
        .then((credential) => {
          return this.handleCompleteWebauthnLogin({
            sessionId: data.sessionId,
            credential: JSON.stringify(credential),
            // username: signInInfo.username,
            exp: `${getExpireLength()}`,
          });
        })
        .catch((err) => {
          if (!this.isExpectedWebauthnCancellation(err)) {
            sentry.error({
              topic: 'auth',
              error: 'WebAuthn login failed',
              team: 'users-infra',
              addContext: {
                name: 'auth',
                context: {
                  errMessage: err.message,
                },
              },
            });
          }
          console.error(err.message);
          throw new Error('Login failed', { cause: LOGIN_ERROR_CAUSE.webauthn });
        });
    });
  }

  private isExpectedWebauthnCancellation(error: unknown): boolean {
    if (!(error instanceof Error)) {
      return false;
    }

    if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
      return true;
    }

    // these typically mean the user cancelled the operation
    return error.message.includes('timed out') || error.message.includes('not allowed');
  }

  private handleCompleteWebauthnLogin(req: CompleteWebAuthnLoginRequest) {
    return AuthService.CompleteWebAuthnLogin(
      this.wrappedFetch() as (url: string, reqInit: RequestInit) => Promise<CompleteWebAuthnLoginResponse>,
      req
    ).then((data) => {
      if (!!data?.token) {
        setWeaveToken(data.token, data.jtiToken || '');
        const verifyResponse: VerifyAuthResponse = {
          token: data.token,
        };
        return verifyResponse;
      }
      throw new Error('No token present after webauthn login');
    });
  }

  private handleWebauthnRegistration(token: string) {
    return AuthService.BeginWebAuthnRegistration(
      this.wrappedFetch(token) as (url: string, reqInit: RequestInit) => Promise<BeginWebAuthnRegistrationResponse>,
      {}
    ).then((data) => {
      let jsonOptions = '';
      if (data.options) {
        jsonOptions = atob(data.options);
      }
      const parsedOptions = JSON.parse(jsonOptions);
      const options = parseCreationOptionsFromJSON(parsedOptions);

      // webauthn registration challenge
      return create(options).then((credential: RegistrationPublicKeyCredential) => {
        const displayName = this.getCredentialDisplayName(credential);
        return this.handleCompleteWebauthnRegistration(token, {
          sessionId: data.sessionId,
          credential: JSON.stringify(credential),
          credentialDisplayName: displayName,
        });
      });
    });
  }

  private getCredentialDisplayName(credential: RegistrationPublicKeyCredential) {
    let displayName = '';
    if (credential.authenticatorAttachment === 'cross-platform') {
      // figure out what names to give to cross-platform keys
      displayName = 'Cross-Platform Authenticator';
    } else if (credential.authenticatorAttachment === 'platform') {
      // using User-Agent we can guess at the platform
      if (/iPhone|iPad|Mac/.test(navigator.userAgent)) {
        displayName = 'iCloud';
      } else if (/Windows/.test(navigator.userAgent)) {
        displayName = 'Windows Hello';
      } else if (/Android/.test(navigator.userAgent)) {
        displayName = 'Android';
      } else if (/CrOS/.test(navigator.userAgent)) {
        displayName = 'Chrome OS';
      } else {
        displayName = 'Unknown Platform Authenticator';
      }
    }
    // append the year and month to the key name e.g. 2024-09 with zero padded numbers
    const d = new Date();
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const monthStr = month.toString().padStart(2, '0');
    displayName += ` ${year}-${monthStr}`;
    return displayName;
  }

  private handleCompleteWebauthnRegistration(token: string, req: CompleteWebAuthnRegistrationRequest) {
    return AuthService.CompleteWebAuthnRegistration(
      this.wrappedFetch(token) as (url: string, reqInit: RequestInit) => Promise<CompleteWebAuthnRegistrationResponse>,
      req
    ).then((data) => {
      return data;
    });
  }

  private wrappedFetch(token?: string): (url: string, reqInit: RequestInit) => Promise<Response> {
    return (url: string, reqInit: RequestInit) => {
      if (!!token) {
        reqInit.headers = {
          ...reqInit.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      return fetch(`${appConfig.BACKEND_API}${url}`, reqInit).then((res) => {
        if (res.status !== 200) {
          return res.json().then((resBody: { code: number; message: string }) => {
            throw new Error(resBody.message);
          });
        }
        return res.json();
      });
    };
  }
}
